import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Hook to handle navigation to posts from within the app (e.g., from chat).
 * Standardizes the "no-reload" transition and ensures relative paths are used.
 */
export const useNavigateToPost = () => {
    const navigate = useNavigate();

    const navigateToPost = useCallback((url) => {
        if (!url) return;

        try {
            // Ensure we use a relative path to prevent full page reload
            const urlObj = new URL(url, window.location.origin);

            // Recognize internal origins even when absolute (e.g. production links on localhost)
            const internalHostnames = [
                'wehearthem.com',
                'www.wehearthem.com',
                'wehearthem.web.app',
                'wehearthem-dev.web.app',
                'localhost'
            ];

            const isInternalHost =
                urlObj.origin === window.location.origin ||
                internalHostnames.includes(urlObj.hostname);

            if (isInternalHost) {
                let pathname = urlObj.pathname;
                let search = urlObj.search;

                // Standardize navigation: Convert /post/:id to /home?post=:id
                // to trigger the feed-based highlighting and scroll behavior.
                if (pathname.startsWith('/post/')) {
                    const postId = pathname.split('/')[2];
                    if (postId) {
                        pathname = '/home';
                        const params = new URLSearchParams(search);
                        params.set('post', postId);
                        search = '?' + params.toString();
                    }
                }

                const path = pathname + search + urlObj.hash;
                navigate(path);
            } else {
                // External link: open in new tab
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            console.error('[Navigation] Failed to parse URL:', err);
            // Fallback: try navigate directly or open in new tab
            if (url.startsWith('/') || url.startsWith('?')) {
                navigate(url);
            } else {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        }
    }, [navigate]);

    return { navigateToPost };
};
